import React, { useEffect, useState } from "react";
import {
  Dialog,
  useMediaQuery,
  useTheme,
  Theme,
  Box,
  CircularProgress,
  Typography,
  IconButton,
  DialogTitle,
  DialogContent,
  Button,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { AtticusForm } from "../../models/AtticusForm";
import { formsApi } from "../../apis";
import { Estate } from "../../models/Estate";
import Cookies from "js-cookie";
import { CONSTANTS as SHARED_CONSTANTS } from "@theatticusapp/atticus-shared-components";
import { useAuthContext } from "AuthContextWrapper";

const dialogStyles = {
  height: "75vh",
  width: "100%",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  padding: (theme: Theme) => theme.spacing(2),
} as const;

interface Props {
  open: boolean;
  onClose: () => void;
  onRemove?: (formId?: number) => void;
  removeText?: string;
  removeConfirmationText?: string;
  form?: AtticusForm;
  estate?: Estate;
  showAddToFileCabinet?: boolean;
}

export function AtticusFormViewDialog({
  open,
  onClose,
  onRemove,
  removeText = "hide",
  form,
  estate,
  removeConfirmationText = "Are you sure?",
  showAddToFileCabinet = false,
}: Props): JSX.Element {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const [formUrl, setFormUrl] = useState<string>();
  const [showRemoveConfirmation, setShowRemoveConfirmation] = useState(false);
  const [addedToFileCabinet, setAddedToFileCabinet] = useState(false);
  const { activeEstate, user } = useAuthContext()

  useEffect(() => {
    (async () => {
      // Get Processed Url
      if (form?.getFilledFormUrl) {
        const authToken = Cookies.get(SHARED_CONSTANTS.COOKIES.authToken) || "";
        if (!activeEstate?.id || !user?.id) {
          return
        }
        const url = form.getFilledFormUrl(activeEstate?.id, user?.id, authToken);
        setFormUrl(url || undefined);
      } else {
        setFormUrl(undefined);
      }
      setShowRemoveConfirmation(false);
    })();
    return () => {
      // reset state for the next render
      setAddedToFileCabinet(false);
      setShowRemoveConfirmation(false);
    };
  }, [form]);

  const saveToFileCabinet = async (): Promise<void> => {
    if (form?.id && estate?.id && !addedToFileCabinet) {
      if (await formsApi.addFormForEstate(estate.id, form.id )) {
        setAddedToFileCabinet(true);
      }
    }
  };

  const getRemoveButtons = (): JSX.Element => {
    return (
      <>
        {!showRemoveConfirmation ? (
          <Button
            onClick={() => {
              setShowRemoveConfirmation(true);
            }}
            color="secondary"
          >
            {removeText}
          </Button>
        ) : (
          <>
            {removeConfirmationText}
            <Button
              variant="outlined"
              onClick={() => {
                onRemove?.(form?.id);
              }}
              color="secondary"
              sx={{
                minWidth: "auto",
              }}
            >
              Yes
            </Button>
            <Button
              variant="outlined"
              onClick={() => {
                setShowRemoveConfirmation(false);
              }}
              color="secondary"
              sx={{
                minWidth: "auto",
              }}
            >
              No
            </Button>
          </>
        )}
      </>
    );
  };

  return (
    <Dialog
      fullScreen={fullScreen}
      fullWidth
      maxWidth="lg"
      open={open}
      onClose={onClose}
      aria-labelledby="responsive-dialog-title"
    >
      {form ? (
        <>
          <DialogTitle
            sx={{
              margin: 0,
              padding: theme.spacing(2),
            }}
          >
            <Box
              sx={{
                paddingRight: "2rem",
              }}
            >
              <>
                <Typography variant="h5">
                  {form.governmentId ? `[${form.governmentId}] - ` : ``}
                  {form.formTitle}
                </Typography>
                <Typography variant="h6">{form.geography?.fullName}</Typography>
              </>
            </Box>
            {onClose ? (
              <IconButton
                aria-label="close"
                sx={{
                  position: "absolute",
                  right: 0,
                  top: 0,
                  padding: "1rem",
                  color: (theme: Theme) => theme.palette.grey[500],
                }}
                onClick={onClose}
              >
                <CloseIcon />
              </IconButton>
            ) : null}
          </DialogTitle>
          <DialogContent sx={dialogStyles}>
            {formUrl ? (
              <>
                <object
                  style={{
                    width: "100%",
                    height: "100%",
                  }}
                  data={formUrl}
                  type="application/pdf"
                >
                  <iframe
                    src={formUrl}
                    style={{
                      width: "100%",
                      height: "100%",
                    }}
                  />
                </object>
                {form?.id && (
                  <Box sx={{ mt: 1 }}>
                    {showAddToFileCabinet && (
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        {addedToFileCabinet && <Typography>✅</Typography>}
                        <Button
                          onClick={() => saveToFileCabinet()}
                          color="secondary"
                        >
                          Add to file Cabinet
                        </Button>
                      </Box>
                    )}
                    {onRemove && getRemoveButtons()}
                  </Box>
                )}
              </>
            ) : (
              <CircularProgress />
            )}
          </DialogContent>
        </>
      ) : (
        <DialogContent sx={dialogStyles}>Form Required.</DialogContent>
      )}
    </Dialog>
  );
}
